import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { getApiUrl } from "@/config/runtime-env";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useEndChatSession } from "@/hooks/useChat";
import { webSocketService, type ChatMessage } from "@/services/websocket";
import { useQueryClient } from "@tanstack/react-query";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import { AlertCircle, Send, Smile, X, Paperclip, Loader2, Info, FileIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ConfirmEndSessionDialog } from "./ConfirmEndSessionDialog";
import MessageBubble from "./MessageBubble";
import TypingIndicator from "./TypingIndicator";
import {
  useApproveSessionRequest,
  useRejectSessionRequest,
} from "@/hooks/useSessions";

interface Student {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  userType: string;
  profilePicture: string;
}

interface ChatSidebarProps {
  student: Student;
  sessionId: string;
  sessionStatus?: string;
  onClose: () => void;
  onStartVoiceCall?: () => void;
  onStartVideoCall?: () => void;
  refetchChatSessions?: () => void;
}

interface Message {
  id: string;
  sessionId: string;
  senderId: string;
  sessionStatus: string;
  message: string;
  messageType: "text" | "image" | "file" | "system";
  sentAt: string;
  fileId?: string;
  file?: {
    id: string;
    fileName: string;
    fileType: string;
    fileSize: number;
  };
}

export default function ChatSidebar({
  student,
  sessionId,
  sessionStatus,
  onClose,
  onStartVoiceCall: _onStartVoiceCall,
  onStartVideoCall: _onStartVideoCall,
  refetchChatSessions,
}: ChatSidebarProps) {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const [message, setMessage] = useState("");
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { user } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<number>();
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const chatHandlersIdRef = useRef<string | null>(null);

  // File upload states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Chat mutations
  const endChatSessionMutation = useEndChatSession();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Check if session is ended
  const isSessionEnded =
    sessionStatus === "ended" || sessionStatus === "completed";

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target as Node)
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const onEmojiClick = (emojiData: EmojiClickData) => {
    setMessage((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  // Load existing messages when component mounts
  useEffect(() => {
    const loadSessionMessages = async () => {
      try {
        const response = await fetch(
          `${getApiUrl()}/chat/sessions/${sessionId}/messages`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
            },
          }
        );

        if (response.ok) {
          const responseData = await response.json();
          const messagesData = responseData.data || responseData;
          setMessages(messagesData?.messages || messagesData || []);
        }
      } catch (error) {
        console.error("Failed to load session messages:", error);
      }
    };

    if (sessionId) {
      loadSessionMessages();
    }
  }, [sessionId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = () => {
    if (message.trim() && user) {
      const messageText = message.trim();

      // Create optimistic message for immediate UI update
      const optimisticMessage: Message = {
        id: `temp-${Date.now()}-${Math.random()}`,
        sessionId,
        senderId: user.id,
        sessionStatus: "active",
        message: messageText,
        messageType: "text",
        sentAt: new Date().toISOString(),
      };

      // Add optimistic message to UI immediately
      setMessages((prev) => [...prev, optimisticMessage]);

      // Clear input
      setMessage("");

      // Send to server
      const payload = {
        sessionId,
        message: messageText,
        messageType: "text" as const,
      };
      webSocketService.sendMessage(payload);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTyping = (value: string) => {
    setMessage(value);

    if (value && !isUserTyping) {
      setIsUserTyping(true);
      webSocketService.startTyping(sessionId);
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout
    typingTimeoutRef.current = setTimeout(() => {
      if (isUserTyping) {
        setIsUserTyping(false);
        webSocketService.stopTyping(sessionId);
      }
    }, 1000);
  };

  // File upload handlers
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf',
      'text/plain',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];

    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Only images (JPEG, PNG, GIF, WebP) and documents (PDF, TXT, DOC, DOCX, XLS, XLSX) are allowed.",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: "File size must be less than 10MB.",
        variant: "destructive",
      });
      return;
    }

    setSelectedFile(file);
  };

  const handleSendFileMessage = async () => {
    if (!selectedFile || !user) return;

    setIsUploadingFile(true);
    setUploadProgress(0);

    try {
      // Step 1: Upload file to server (simulate progress)
      const formData = new FormData();
      formData.append('file', selectedFile);

      setUploadProgress(30);

      const uploadResponse = await fetch(
        `${getApiUrl()}/chat/sessions/${sessionId}/upload-file`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          },
          body: formData,
        }
      );

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      const response = await uploadResponse.json();
      
      const { fileId, fileName } = response?.data;
      setUploadProgress(70);

      // Step 2: Send message with file attachment
      const messageText = message.trim() || fileName;

      // Create optimistic message
      const optimisticMessage: Message = {
        id: `temp-${Date.now()}-${Math.random()}`,
        sessionId,
        senderId: user.id,
        sessionStatus: "active",
        message: messageText,
        messageType: "file",
        sentAt: new Date().toISOString(),
        fileId,
        file: {
          id: fileId,
          fileName: selectedFile.name,
          fileType: selectedFile.type,
          fileSize: selectedFile.size,
        },
      };

      setMessages((prev) => [...prev, optimisticMessage]);

      webSocketService.sendMessage({
        sessionId,
        message: messageText,
        messageType: 'file',
        fileId,
      });

      setUploadProgress(100);

      // Clear states
      setMessage("");
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      toast({
        title: "File sent",
        description: "Your file has been uploaded and sent successfully.",
      });
    } catch (error) {
      console.error('Failed to send file:', error);
      toast({
        title: "Upload failed",
        description: "Failed to upload file. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingFile(false);
      setUploadProgress(0);
    }
  };

  const getInitials = (name: string) => {
    if (name) {
      return name
        .split(" ")
        .map((n) => n.charAt(0))
        .join("")
        .toUpperCase();
    } else {
      return "";
    }
  };

  const handleEndSession = async () => {
    try {
      await endChatSessionMutation.mutateAsync({ sessionId });
      // Invalidate session history for this counselor
      if (user?.id) {
        queryClient.invalidateQueries({
          queryKey: ["counselor", "session-history", user.id],
        });
      }
      toast({
        title: "Success",
        description: "Chat session has been marked as completed.",
        variant: "default",
      });
      // Optionally close the chat after successful completion
      onClose();
    } catch (error) {
      // Failed to end chat session
      toast({
        title: "Error",
        description: "Failed to mark session as completed. Please try again.",
        variant: "destructive",
      });
    } finally {
      refetchChatSessions?.();
    }
  };

  const approveRequest = useApproveSessionRequest();
  const rejectRequest = useRejectSessionRequest();

  const handleActionApprove = async (actionId: string, action: any) => {
    try {
      if (action.type === "session_request") {
        await approveRequest.mutateAsync({
          requestId: actionId,
          data: {
            scheduledAt: action.dueDate,
            notes: "Approved from dashboard",
          },
        });
      }
    } catch (error) {
      console.error("Error approving action:", error);
    } finally {
      refetchChatSessions?.();
    }
  };

  const handleActionReject = async (actionId: string, action: any) => {
    try {
      if (action.type === "session_request") {
        await rejectRequest.mutateAsync({
          requestId: actionId,
          reason: "Rejected from dashboard",
        });
      }
    } catch (error) {
      console.error("Error rejecting action:", error);
    } finally {
      refetchChatSessions?.();
    }
  };

  // Setup WebSocket event handlers for chat functionality
  useEffect(() => {
    if (!webSocketService.isConnected()) {
      // WebSocket service not connected, skipping chat setup
      return;
    }

    // Add chat event handlers
    const handlerId = webSocketService.addChatHandlers({
      onNewMessage: (payload: { message: ChatMessage }) => {
        setMessages((prev) => {
          // Check if message already exists to avoid duplicates
          const messageExists = prev.some(
            (msg) => msg.id === payload.message.id
          );
          if (messageExists) {
            return prev;
          }

          // If this is a message from the current user, replace any temporary message
          const newMessage = payload.message as Message;
          if (newMessage.senderId === user?.id) {
            // Find and replace temporary message from the same sender with similar content
            const tempMessageIndex = prev.findIndex(
              (msg) =>
                msg.id.startsWith("temp-") &&
                msg.senderId === newMessage.senderId &&
                msg.message === newMessage.message
            );

            if (tempMessageIndex !== -1) {
              // Replace temporary message with real one
              const updatedMessages = [...prev];
              updatedMessages[tempMessageIndex] = newMessage;
              return updatedMessages;
            }
          }

          return [...prev, newMessage];
        });
      },
      onNewMessageNotification: (_data: {
        sessionId: string;
        message: string;
        senderId: string;
      }) => {
        // New message notification received
        // This is for notifications when user is not in session room
        // Usually handled by system notifications, but we can show a toast here if needed
      },
      onSessionJoined: (_data: { sessionId: string }) => {
        // Successfully joined session
        setError(null); // Clear any previous errors
      },
      onJoinSessionFailed: (data: { error: string; sessionId?: string }) => {
        // Failed to join session
        setError(`Failed to join session: ${data.error}`);
      },
      onSessionLeft: (_data: { sessionId: string }) => {
        // Left session
      },
      onUserTyping: (_data: {
        sessionId: string;
        userId: string;
        isTyping: boolean;
      }) => {
        // Handle typing indicators if needed
        // User typing
      },
      onChatError: (error: { message: string; code: number }) => {
        // WebSocket error
        setError(error.message);
      },
    });

    chatHandlersIdRef.current = handlerId;

    // Join the chat session
    webSocketService.joinSession(sessionId);

    return () => {
      // Leave session and cleanup handlers
      webSocketService.leaveSession(sessionId);
      if (chatHandlersIdRef.current) {
        webSocketService.removeChatHandlers(chatHandlersIdRef.current);
        chatHandlersIdRef.current = null;
      }
    };
  }, [sessionId]);

  return (
    <Card className="w-screen md:w-[30rem] h-screen flex flex-col border-l border-gray-200 rounded-none relative overflow-hidden -ml-4 md:ml-0">
      {/* Chat Header */}
      <CardHeader className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Avatar className="w-10 h-10 mr-3">
              <AvatarImage src={student?.profilePicture} alt={student?.name} />
              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                {getInitials(student?.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-semibold text-gray-900">{student?.name}</h3>
              <p className="text-sm text-gray-500">Student</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {/* {onStartVoiceCall && (
              <Button size="sm" variant="ghost" onClick={onStartVoiceCall}>
                <Phone className="w-4 h-4" />
              </Button>
            )}
            {onStartVideoCall && (
              <Button size="sm" variant="ghost" onClick={onStartVideoCall}>
                <Video className="w-4 h-4" />
              </Button>
            )} */}
            {!isSessionEnded &&
              sessionStatus !== "pending" &&
              sessionStatus !== "rejected" && (
                <>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => setShowConfirmDialog(true)}
                    disabled={endChatSessionMutation.isPending}
                  >
                    {endChatSessionMutation.isPending
                      ? "Ending..."
                      : "Mark Session as Completed"}
                  </Button>
                  <ConfirmEndSessionDialog
                    open={showConfirmDialog}
                    onConfirm={async () => {
                      setShowConfirmDialog(false);
                      await handleEndSession();
                    }}
                    onCancel={() => setShowConfirmDialog(false)}
                  />
                </>
              )}
            <Button size="sm" variant="ghost" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {/* Error Message */}
      {error && (
        <div className="p-2 bg-red-50 border-b border-red-200">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Pending Status Warning */}
      {sessionStatus === "pending" && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
          <div className="flex items-center space-x-2 justify-center">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800 font-medium">
              Note: Please approve to chat with the student.
            </p>
          </div>
          <div className="flex items-center space-x-2 mt-2 justify-center">
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-500"
              onClick={() => {
                handleActionApprove(sessionId, {
                  type: "session_request",
                });
              }}
            >
              Approve
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                handleActionReject(sessionId, {
                  type: "session_request",
                });
              }}
            >
              Reject
            </Button>
          </div>
        </div>
      )}

      {/* Chat Messages */}
      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
        <ScrollArea className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-4">
            {messages?.map((msg: Message) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                isOwnMessage={msg.senderId === user?.id}
                senderName={msg.senderId === user?.id ? "You" : student?.name}
                senderAvatar={msg.senderId === user?.id ? "" : ""}
              />
            ))}

            {false && (
              <div className="flex items-start space-x-2">
                <Avatar className="w-8 h-8">
                  <AvatarImage src="" alt={student?.name} />
                  <AvatarFallback className="bg-gray-200 text-gray-600 text-xs">
                    {getInitials(student?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="bg-gray-100 p-3 rounded-lg">
                  <TypingIndicator />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </CardContent>

      {/* Chat Input */}
      <div
        className={`p-4 border-t sticky bottom-0 z-10 ${
          isSessionEnded || sessionStatus === "rejected"
            ? "bg-red-50 border-red-200"
            : sessionStatus === "pending"
            ? "bg-amber-50 border-amber-200"
            : "bg-white border-gray-200"
        }`}
      >
        {isSessionEnded ? (
          <div className="text-center p-4">
            <p className="text-sm text-red-600 font-semibold">
              This session has ended.
            </p>
          </div>
        ) : sessionStatus === "pending" ? (
          <div className="text-center p-4">
            <p className="text-sm text-amber-800 font-semibold">
              Student has requested a chat session. Please approval to continue
            </p>
          </div>
        ) : sessionStatus === "rejected" ? (
          <div className="text-center p-4">
            <p className="text-sm text-red-600 font-semibold">
              This chat session has been rejected.
            </p>
          </div>
        ) : (
          <>
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* User education notice about file uploads */}
            <div className="mb-2 flex items-start space-x-1 text-xs text-gray-500">
              <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>
                Max 10MB - Images & Documents only. Files auto-delete after 90 days.
              </span>
            </div>

            {/* File preview when file is selected */}
            {selectedFile && (
              <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <FileIcon className="w-5 h-5 text-gray-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSelectedFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    disabled={isUploadingFile}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                {/* Upload progress bar */}
                {isUploadingFile && (
                  <div className="mt-2">
                    <Progress value={uploadProgress} className="h-1" />
                    <p className="text-xs text-gray-500 mt-1">
                      Uploading... {uploadProgress}%
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center space-x-2">
              {/* Paperclip button for file upload */}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingFile || !!selectedFile}
                className="flex-shrink-0"
              >
                <Paperclip className="w-4 h-4" />
              </Button>

              <div className="flex-1 relative">
                <Input
                  value={message}
                  onChange={(e) => handleTyping(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={selectedFile ? "Add a message (optional)..." : "Type your message..."}
                  className="pr-10"
                  disabled={isUploadingFile}
                />
                <div className="relative" ref={emojiPickerRef}>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="absolute right-2 top-[-19.6px] transform -translate-y-1/2"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    disabled={isUploadingFile}
                  >
                    {showEmojiPicker ? (
                      <X className="w-4 h-4" />
                    ) : (
                      <Smile className="w-4 h-4" />
                    )}
                  </Button>
                  {showEmojiPicker && (
                    <div className="absolute bottom-full right-0 mb-2 z-50">
                      <EmojiPicker
                        onEmojiClick={onEmojiClick}
                        autoFocusSearch={false}
                        width={300}
                        height={350}
                      />
                    </div>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                onClick={selectedFile ? handleSendFileMessage : handleSendMessage}
                disabled={(!message.trim() && !selectedFile) || isUploadingFile}
                className="bg-primary hover:bg-primary/90"
              >
                {isUploadingFile ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
